import React, { useEffect, useRef, useState } from "react";

/**
 * Tiny modal asking for a positive integer. Used by the Equipment view's
 * drag-to-allocate flow. Enter confirms, Esc cancels, 0 removes.
 *
 * Props:
 *   open           — bool
 *   title          — header label (e.g. "Wireless mic → Stage A")
 *   helper         — small hint text under the input
 *   defaultValue   — initial qty (auto-selected so user can type to replace)
 *   onConfirm(qty) — int (>=0) or null on cancel
 *   onCancel()
 */
export default function QtyPromptDialog({
  open,
  title,
  helper,
  defaultValue = 1,
  onConfirm,
  onCancel,
}) {
  const [value, setValue] = useState(String(defaultValue));
  const ref = useRef(null);

  useEffect(() => {
    if (open) {
      setValue(String(defaultValue));
      // Defer focus so the dialog mount transition can finish.
      setTimeout(() => {
        ref.current?.focus();
        ref.current?.select();
      }, 30);
    }
  }, [open, defaultValue]);

  if (!open) return null;

  const submit = () => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n) || n < 0) {
      // Treat junk input as cancel — safer than rounding silently.
      onCancel();
      return;
    }
    onConfirm(n);
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="qty-prompt-overlay"
    >
      <div
        className="bg-bg-elevated border border-amber min-w-[320px] max-w-[420px] w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="qty-prompt-dialog"
      >
        <header className="px-3 py-2 border-b border-line-strong flex items-center">
          <span className="mono text-2xs uppercase tracking-[0.2em] text-amber truncate">{title}</span>
        </header>
        <div className="p-4 space-y-3">
          <div>
            <label className="mono text-2xs uppercase tracking-widest text-ink-mute block mb-1">
              QUANTITY
            </label>
            <input
              ref={ref}
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submit(); }
                else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
              }}
              className="w-full bg-bg-base border border-line-strong focus:border-amber outline-none text-ink mono text-2xl px-3 py-2 tabular-nums"
              data-testid="qty-prompt-input"
              min="0"
            />
          </div>
          {helper && (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute leading-relaxed">
              {helper}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="mono text-2xs text-ink-mute">↩ confirm · ESC cancel · 0 removes</span>
            <div className="flex gap-1">
              <button
                onClick={onCancel}
                className="mono text-2xs uppercase tracking-widest px-3 py-1.5 border border-line text-ink-mute hover:text-ink hover:border-ink-mute transition-colors"
                data-testid="qty-prompt-cancel"
              >
                CANCEL
              </button>
              <button
                onClick={submit}
                className="mono text-2xs uppercase tracking-widest px-4 py-1.5 bg-amber text-bg-base hover:bg-amber-light transition-colors"
                data-testid="qty-prompt-confirm"
              >
                CONFIRM
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
